import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  X,
  RefreshCw,
  Plus,
  Loader2,
} from 'lucide-react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchDeliverySettlement,
  fetchDeliverySettlements,
  fetchPendingDeliveryCollections,
  createDeliverySettlement,
  updateDeliverySettlement,
} from '../services/deliverySettlementService';
import { fetchDeliveryBoys } from '../services/deliveryBoyService';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
};

const formatAmount = (value) => {
  const amount = Number.parseFloat(value);
  if (Number.isNaN(amount)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(amount);
};

const settlementModes = [
  { value: '', label: 'All modes' },
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'mixed', label: 'Mixed' },
];

const formatDateTimeLocalValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (num) => num.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const EMPTY_COLLECTION_SUMMARY = {
  collectionCount: 0,
  totalAmount: 0,
  cashAmount: 0,
  upiAmount: 0,
  mixedAmount: 0,
};

function CreateSettlementDrawer({ isOpen, onClose, onCreated }) {
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [loadingBoys, setLoadingBoys] = useState(false);
  const [deliveryBoyId, setDeliveryBoyId] = useState('');
  const [filters, setFilters] = useState({ fromDate: '', toDate: '' });
  const [collections, setCollections] = useState([]);
  const [summary, setSummary] = useState({ ...EMPTY_COLLECTION_SUMMARY });
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [fetchingCollections, setFetchingCollections] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    settlementMode: '',
    settledAt: formatDateTimeLocalValue(new Date()),
    referenceNumber: '',
    notes: '',
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setForm({
      settlementMode: '',
      settledAt: formatDateTimeLocalValue(new Date()),
      referenceNumber: '',
      notes: '',
    });
    setFilters({ fromDate: '', toDate: '' });
    setCollections([]);
    setSummary({ ...EMPTY_COLLECTION_SUMMARY });
    setSelectedCollectionIds([]);
    setDeliveryBoyId('');

    const loadDeliveryBoys = async () => {
      setLoadingBoys(true);
      try {
        const response = await fetchDeliveryBoys({ page: 1, limit: 200 });
        const records = response.data?.data?.records || [];
        setDeliveryBoys(records);
      } catch (error) {
        console.error(error);
        toast.error('Failed to load delivery executives');
      } finally {
        setLoadingBoys(false);
      }
    };

    loadDeliveryBoys();
  }, [isOpen]);

  const fetchCollections = useCallback(async () => {
    if (!isOpen) {
      return;
    }

    if (!deliveryBoyId) {
      setCollections([]);
      setSummary({ ...EMPTY_COLLECTION_SUMMARY });
      setSelectedCollectionIds([]);
      return;
    }

    setFetchingCollections(true);
    try {
      const params = { deliveryBoyId };
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const response = await fetchPendingDeliveryCollections(params);
      const data = response.data?.data || {};
      const pendingCollections = data.collections || [];
      setCollections(pendingCollections);
      if (data.summary) {
        setSummary({
          collectionCount: data.summary.collectionCount ?? pendingCollections.length,
          totalAmount: Number.parseFloat(data.summary.totalAmount ?? 0) || 0,
          cashAmount: Number.parseFloat(data.summary.cashAmount ?? 0) || 0,
          upiAmount: Number.parseFloat(data.summary.upiAmount ?? 0) || 0,
          mixedAmount: Number.parseFloat(data.summary.mixedAmount ?? 0) || 0,
        });
      } else {
        setSummary({
          ...EMPTY_COLLECTION_SUMMARY,
          collectionCount: pendingCollections.length,
        });
      }
      setSelectedCollectionIds(pendingCollections.map((collection) => collection.id));
    } catch (error) {
      console.error(error);
      toast.error('Failed to load pending collections');
      setCollections([]);
      setSummary({ ...EMPTY_COLLECTION_SUMMARY });
      setSelectedCollectionIds([]);
    } finally {
      setFetchingCollections(false);
    }
  }, [deliveryBoyId, filters.fromDate, filters.toDate, isOpen]);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const selectedSummary = useMemo(() => {
    const selectedSet = new Set(selectedCollectionIds);
    let total = 0;
    let cash = 0;
    let upi = 0;
    let mixed = 0;

    collections.forEach((collection) => {
      if (!selectedSet.has(collection.id)) return;
      const amount = Number.parseFloat(collection.amount || 0) || 0;
      total += amount;
      const mode = (collection.mode || '').toString().trim().toLowerCase();
      if (mode === 'cash') {
        cash += amount;
      } else if (mode === 'upi') {
        upi += amount;
      } else if (mode === 'mixed') {
        mixed += amount;
      }
    });

    return {
      totalAmount: Number(total.toFixed(2)),
      cashAmount: Number(cash.toFixed(2)),
      upiAmount: Number(upi.toFixed(2)),
      mixedAmount: Number(mixed.toFixed(2)),
    };
  }, [collections, selectedCollectionIds]);

  const toggleCollectionSelection = (collectionId) => {
    setSelectedCollectionIds((prev) => {
      if (prev.includes(collectionId)) {
        return prev.filter((id) => id !== collectionId);
      }
      return [...prev, collectionId];
    });
  };

  const toggleSelectAll = () => {
    if (collections.length === 0) {
      setSelectedCollectionIds([]);
      return;
    }
    if (selectedCollectionIds.length === collections.length) {
      setSelectedCollectionIds([]);
    } else {
      setSelectedCollectionIds(collections.map((collection) => collection.id));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!deliveryBoyId) {
      toast.error('Select a delivery executive');
      return;
    }
    if (selectedCollectionIds.length === 0) {
      toast.error('Select at least one pending collection');
      return;
    }
    if (!form.settlementMode) {
      toast.error('Select a settlement mode');
      return;
    }

    setSubmitting(true);
    try {
      await createDeliverySettlement({
        deliveryBoyId,
        collectionIds: selectedCollectionIds,
        settlementMode: form.settlementMode,
        settledAt: form.settledAt || undefined,
        referenceNumber: form.referenceNumber?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
      });
      toast.success('Settlement created successfully');
      onCreated();
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || 'Failed to create settlement';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-4">
      <div className="absolute inset-0" onClick={() => !submitting && onClose()} />
      <div className="relative z-10 w-full max-w-4xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Create Delivery Settlement</h2>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close create settlement"
          >
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto px-6 py-4 space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="deliveryBoy" className="block text-sm font-medium text-gray-700">
                Delivery Executive
              </label>
              <select
                id="deliveryBoy"
                value={deliveryBoyId}
                onChange={(event) => setDeliveryBoyId(event.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={loadingBoys || submitting}
              >
                <option value="">Select delivery executive</option>
                {deliveryBoys.map((boy) => (
                  <option key={boy.id} value={boy.id}>
                    {boy.name} {boy.phone ? `(${boy.phone})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="pendingFromDate" className="block text-sm font-medium text-gray-700">
                  From date
                </label>
                <input
                  id="pendingFromDate"
                  type="date"
                  value={filters.fromDate}
                  onChange={(event) => setFilters((prev) => ({ ...prev, fromDate: event.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={fetchingCollections || submitting}
                />
              </div>
              <div>
                <label htmlFor="pendingToDate" className="block text-sm font-medium text-gray-700">
                  To date
                </label>
                <input
                  id="pendingToDate"
                  type="date"
                  value={filters.toDate}
                  onChange={(event) => setFilters((prev) => ({ ...prev, toDate: event.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  disabled={fetchingCollections || submitting}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-600">
              {deliveryBoyId
                ? `${summary.collectionCount} pending collection${summary.collectionCount === 1 ? '' : 's'} found`
                : 'Select a delivery executive to view pending collections'}
            </div>
            <button
              type="button"
              onClick={fetchCollections}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
              disabled={!deliveryBoyId || fetchingCollections || submitting}
            >
              {fetchingCollections ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <RefreshCw size={16} className="mr-2" />
              )}
              Refresh
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-700">Pending summary</h3>
              <dl className="mt-3 space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <dt>Total amount</dt>
                  <dd className="font-medium">{formatAmount(summary.totalAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Cash</dt>
                  <dd>{formatAmount(summary.cashAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>UPI</dt>
                  <dd>{formatAmount(summary.upiAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Mixed</dt>
                  <dd>{formatAmount(summary.mixedAmount)}</dd>
                </div>
              </dl>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
              <h3 className="text-sm font-semibold text-indigo-700">Selected collections</h3>
              <dl className="mt-3 space-y-2 text-sm text-indigo-700">
                <div className="flex justify-between">
                  <dt>Total amount</dt>
                  <dd className="font-semibold">{formatAmount(selectedSummary.totalAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Cash</dt>
                  <dd>{formatAmount(selectedSummary.cashAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>UPI</dt>
                  <dd>{formatAmount(selectedSummary.upiAmount)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Mixed</dt>
                  <dd>{formatAmount(selectedSummary.mixedAmount)}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-2 text-sm">
              <span className="font-medium text-gray-700">Pending collections</span>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-indigo-600 hover:text-indigo-700"
                disabled={collections.length === 0 || submitting}
              >
                {selectedCollectionIds.length === collections.length ? 'Clear selection' : 'Select all'}
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {fetchingCollections ? (
                <div className="flex items-center justify-center py-8 text-sm text-gray-500">
                  <Loader2 size={18} className="mr-2 animate-spin" /> Loading collections...
                </div>
              ) : collections.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500">
                  No pending collections available
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-4 py-3">Select</th>
                      <th className="px-4 py-3">Order</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Mode</th>
                      <th className="px-4 py-3">Collected At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {collections.map((collection) => {
                      const isSelected = selectedCollectionIds.includes(collection.id);
                      return (
                        <tr key={collection.id} className={isSelected ? 'bg-indigo-50/60' : ''}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              checked={isSelected}
                              onChange={() => toggleCollectionSelection(collection.id)}
                              disabled={submitting}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{collection.order?.showId || collection.orderId}</div>
                            <div className="text-xs text-gray-500">
                              {collection.order?.customer?.name || '—'}
                              {collection.order?.customer?.phone ? ` (${collection.order.customer.phone})` : ''}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-900">
                            {formatAmount(collection.amount)}
                          </td>
                          <td className="px-4 py-3 capitalize text-gray-700">{collection.mode || '—'}</td>
                          <td className="px-4 py-3 text-gray-600">{formatDateTime(collection.collectedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="settlementMode" className="block text-sm font-medium text-gray-700">
                Settlement mode
              </label>
              <select
                id="settlementMode"
                value={form.settlementMode}
                onChange={(event) => setForm((prev) => ({ ...prev, settlementMode: event.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={submitting}
              >
                <option value="">Select mode</option>
                {settlementModes
                  .filter((mode) => mode.value)
                  .map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label htmlFor="settledAt" className="block text-sm font-medium text-gray-700">
                Settlement time
              </label>
              <input
                id="settledAt"
                type="datetime-local"
                value={form.settledAt}
                onChange={(event) => setForm((prev) => ({ ...prev, settledAt: event.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={submitting}
              />
            </div>
            <div>
              <label htmlFor="referenceNumber" className="block text-sm font-medium text-gray-700">
                Reference number
              </label>
              <input
                id="referenceNumber"
                type="text"
                value={form.referenceNumber}
                onChange={(event) => setForm((prev) => ({ ...prev, referenceNumber: event.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={submitting}
                placeholder="Optional"
              />
            </div>
            <div>
              <label htmlFor="settlementNotes" className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                id="settlementNotes"
                value={form.notes}
                onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                disabled={submitting}
                placeholder="Optional notes"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={() => !submitting && onClose()}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
              disabled={submitting}
            >
              {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
              Create settlement
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DeliverySettlements() {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('settledAt');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const initialEditForm = useMemo(
    () => ({ settlementMode: '', settledAt: '', referenceNumber: '', notes: '' }),
    [],
  );
  const [showEdit, setShowEdit] = useState(false);
  const [editingSettlementId, setEditingSettlementId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState(initialEditForm);

  const loadSettlements = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        page: overrides.page ?? page,
        limit: overrides.limit ?? limit,
        sortBy: overrides.sortBy ?? sortBy,
        sortOrder: overrides.sortOrder ?? sortOrder,
        search:
          overrides.search !== undefined
            ? overrides.search
            : search.trim(),
        mode:
          overrides.mode !== undefined ? overrides.mode : modeFilter,
        fromDate:
          overrides.fromDate !== undefined ? overrides.fromDate : fromDate,
        toDate: overrides.toDate !== undefined ? overrides.toDate : toDate,
      };

      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === '' || value === null || value === undefined) {
          delete params[key];
        }
      });

      const response = await fetchDeliverySettlements(params);
      const data = response.data?.data || {};
      setSettlements(data.records || []);
      const meta = data.meta || {};
      setTotalPages(meta.pages || 1);
      setTotalRecords(meta.total || 0);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load delivery settlements');
    } finally {
      setLoading(false);
    }
  };

  const handleSettlementCreated = () => {
    setShowCreate(false);
    if (page !== 1) {
      setPage(1);
    } else {
      loadSettlements({ page: 1 });
    }
  };

  useEffect(() => {
    loadSettlements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sortBy, sortOrder, modeFilter, fromDate, toDate]);

  useEffect(() => {
    if (search === '') {
      setPage(1);
      loadSettlements({ page: 1, search: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmed = search.trim();
    setPage(1);
    loadSettlements({ page: 1, search: trimmed });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleLimitChange = (event) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleModeChange = (event) => {
    setModeFilter(event.target.value);
    setPage(1);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
    setPage(1);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setModeFilter('');
    setFromDate('');
    setToDate('');
    setSortBy('settledAt');
    setSortOrder('DESC');
    setPage(1);
    loadSettlements({
      page: 1,
      search: '',
      mode: '',
      fromDate: '',
      toDate: '',
      sortBy: 'settledAt',
      sortOrder: 'DESC',
    });
  };

  const openSettlementDetails = async (id) => {
    setShowDetails(true);
    setDetailLoading(true);
    setSelectedSettlement(null);
    try {
      const response = await fetchDeliverySettlement(id);
      setSelectedSettlement(response.data?.data || null);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch settlement details');
      setShowDetails(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedSettlement(null);
  };

  const resetEditState = useCallback(() => {
    setEditForm(initialEditForm);
    setEditingSettlementId(null);
    setEditLoading(false);
    setEditSaving(false);
  }, [initialEditForm]);

  const openSettlementEditor = async (id) => {
    setShowEdit(true);
    setEditLoading(true);
    setEditingSettlementId(id);
    try {
      const response = await fetchDeliverySettlement(id);
      const settlement = response.data?.data;
      if (!settlement) {
        throw new Error('Missing settlement details');
      }
      setEditForm({
        settlementMode: settlement.settlementMode || '',
        settledAt: formatDateTimeLocalValue(settlement.settledAt) || '',
        referenceNumber: settlement.referenceNumber || '',
        notes: settlement.notes || '',
      });
    } catch (error) {
      console.error(error);
      toast.error('Failed to load settlement for editing');
      setShowEdit(false);
      resetEditState();
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    setShowEdit(false);
    resetEditState();
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editingSettlementId) {
      return;
    }
    if (!editForm.settlementMode) {
      toast.error('Select a settlement mode');
      return;
    }
    if (!editForm.settledAt) {
      toast.error('Select the settlement date and time');
      return;
    }

    setEditSaving(true);
    try {
      await updateDeliverySettlement(editingSettlementId, {
        settlementMode: editForm.settlementMode,
        settledAt: editForm.settledAt,
        referenceNumber: editForm.referenceNumber?.trim() || undefined,
        notes: editForm.notes?.trim() || undefined,
      });
      toast.success('Settlement updated successfully');
      closeEditModal();
      await loadSettlements();
      if (showDetails && selectedSettlement?.id === editingSettlementId) {
        openSettlementDetails(editingSettlementId);
      }
    } catch (error) {
      console.error(error);
      const message = error.response?.data?.message || 'Failed to update settlement';
      toast.error(message);
    } finally {
      setEditSaving(false);
    }
  };

  const startRecord = totalRecords === 0 ? 0 : (page - 1) * limit + 1;
  const endRecord = totalRecords === 0 ? 0 : Math.min(page * limit, totalRecords);

  return (
    <LayoutWithSidebar>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Delivery Settlements
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Review settlement history received from delivery executives.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center space-x-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
            >
              <Plus size={16} />
              <span>Create settlement</span>
            </button>
            <button
              type="button"
              onClick={() => loadSettlements()}
              className="inline-flex items-center justify-center space-x-2 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <form
              onSubmit={handleSearchSubmit}
              className="flex flex-1 items-center space-x-2"
            >
              <div className="relative flex-1">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search by rider, phone, or reference number"
                  className="w-full border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
              >
                Search
              </button>
            </form>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex items-center space-x-2">
                <label htmlFor="modeFilter" className="text-sm text-gray-600 dark:text-gray-300">
                  Mode
                </label>
                <select
                  id="modeFilter"
                  value={modeFilter}
                  onChange={handleModeChange}
                  className="border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {settlementModes.map((mode) => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="fromDate" className="text-sm text-gray-600 dark:text-gray-300">
                  From
                </label>
                <input
                  id="fromDate"
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  className="border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="toDate" className="text-sm text-gray-600 dark:text-gray-300">
                  To
                </label>
                <input
                  id="toDate"
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  className="border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center space-x-2 px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <X size={14} />
                <span>Clear</span>
              </button>
            </div>
          </div>

          <div className="relative overflow-x-auto">
            {loading && (
              <div className="absolute inset-0 bg-white/70 dark:bg-gray-900/60 flex items-center justify-center z-10">
                <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="py-2 px-3">#</th>
                  <th
                    className="py-2 px-3 cursor-pointer"
                    onClick={() => handleSort('deliveryBoy')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Delivery Executive</span>
                      {sortBy === 'deliveryBoy' ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp size={12} />
                        ) : (
                          <ArrowDown size={12} />
                        )
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </div>
                  </th>
                  <th className="py-2 px-3">Phone</th>
                  <th
                    className="py-2 px-3 cursor-pointer"
                    onClick={() => handleSort('totalAmount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Amount</span>
                      {sortBy === 'totalAmount' ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp size={12} />
                        ) : (
                          <ArrowDown size={12} />
                        )
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </div>
                  </th>
                  <th className="py-2 px-3">Mode</th>
                  <th
                    className="py-2 px-3 cursor-pointer"
                    onClick={() => handleSort('settledAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Settled At</span>
                      {sortBy === 'settledAt' ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp size={12} />
                        ) : (
                          <ArrowDown size={12} />
                        )
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </div>
                  </th>
                  <th
                    className="py-2 px-3 cursor-pointer"
                    onClick={() => handleSort('referenceNumber')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Reference</span>
                      {sortBy === 'referenceNumber' ? (
                        sortOrder === 'ASC' ? (
                          <ArrowUp size={12} />
                        ) : (
                          <ArrowDown size={12} />
                        )
                      ) : (
                        <ArrowUpDown size={12} />
                      )}
                    </div>
                  </th>
                  <th className="py-2 px-3">Collections</th>
                  <th className="py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {settlements.length === 0 && !loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-6 px-3 text-center text-gray-500 dark:text-gray-300"
                    >
                      No settlements found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  settlements.map((settlement, index) => (
                    <tr
                      key={settlement.id}
                      className="border-t border-gray-100 dark:border-gray-700"
                    >
                      <td className="py-2 px-3">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td className="py-2 px-3 font-medium">
                        {settlement.deliveryBoy?.name || '—'}
                      </td>
                      <td className="py-2 px-3">
                        {settlement.deliveryBoy?.phone || '—'}
                      </td>
                      <td className="py-2 px-3">{formatAmount(settlement.totalAmount)}</td>
                      <td className="py-2 px-3 uppercase">
                        {settlement.settlementMode || '—'}
                      </td>
                      <td className="py-2 px-3">{formatDateTime(settlement.settledAt)}</td>
                      <td className="py-2 px-3">{settlement.referenceNumber || '—'}</td>
                      <td className="py-2 px-3">{settlement.collectionCount || 0}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openSettlementDetails(settlement.id)}
                            className="inline-flex items-center space-x-1 px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800"
                          >
                            <Eye size={14} />
                            <span>View</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => openSettlementEditor(settlement.id)}
                            className="inline-flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
                          >
                            <span>Edit</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Showing {startRecord} to {endRecord} of {totalRecords} records
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Rows per page
                </span>
                <select
                  value={limit}
                  onChange={handleLimitChange}
                  className="border rounded-md px-2 py-1 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {[10, 20, 50].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
                >
                  Prev
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page === totalPages || totalPages === 0}
                  onClick={() => setPage((prev) => prev + 1)}
                  className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateSettlementDrawer
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={handleSettlementCreated}
        />
      )}

      {showDetails && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-40 px-4">
          <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold">Settlement Details</h2>
              <button
                type="button"
                onClick={closeDetails}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {detailLoading && (
                <div className="flex items-center justify-center py-10">
                  <div className="h-10 w-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {!detailLoading && selectedSettlement && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Delivery Executive</p>
                      <p className="font-medium">
                        {selectedSettlement.deliveryBoy?.name || '—'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedSettlement.deliveryBoy?.phone || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Settled At</p>
                      <p className="font-medium">{formatDateTime(selectedSettlement.settledAt)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className="font-medium">{formatAmount(selectedSettlement.totalAmount)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mode</p>
                      <p className="font-medium uppercase">
                        {selectedSettlement.settlementMode || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Reference Number</p>
                      <p className="font-medium">{selectedSettlement.referenceNumber || '—'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Notes</p>
                      <p className="font-medium text-gray-700 dark:text-gray-300">
                        {selectedSettlement.notes || '—'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-2">Collections</h3>
                    {selectedSettlement.collections?.length ? (
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 dark:bg-gray-800">
                            <tr>
                              <th className="py-2 px-3 text-left">Order</th>
                              <th className="py-2 px-3 text-left">Customer</th>
                              <th className="py-2 px-3 text-left">Amount</th>
                              <th className="py-2 px-3 text-left">Mode</th>
                              <th className="py-2 px-3 text-left">Collected At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSettlement.collections.map((collection) => (
                              <tr
                                key={collection.id}
                                className="border-t border-gray-100 dark:border-gray-700"
                              >
                                <td className="py-2 px-3">
                                  {collection.order?.showId || `#${collection.orderId}`}
                                </td>
                                <td className="py-2 px-3">
                                  {collection.order?.customer?.name || '—'}
                                  <div className="text-xs text-gray-500">
                                    {collection.order?.customer?.phone || ''}
                                  </div>
                                </td>
                                <td className="py-2 px-3">{formatAmount(collection.amount)}</td>
                                <td className="py-2 px-3 uppercase">{collection.mode || '—'}</td>
                                <td className="py-2 px-3">{formatDateTime(collection.collectedAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No collection records linked to this settlement.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-40 px-4">
          <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold">Edit Settlement</h2>
              <button
                type="button"
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-4 space-y-4">
              {editLoading ? (
                <div className="flex items-center justify-center py-10">
                  <div className="h-10 w-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Settlement mode
                      </label>
                      <select
                        value={editForm.settlementMode}
                        onChange={(event) =>
                          setEditForm((prev) => ({ ...prev, settlementMode: event.target.value }))
                        }
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        disabled={editSaving}
                      >
                        <option value="">Select mode</option>
                        {settlementModes
                          .filter((option) => option.value)
                          .map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Settled at
                      </label>
                      <input
                        type="datetime-local"
                        value={editForm.settledAt}
                        onChange={(event) =>
                          setEditForm((prev) => ({ ...prev, settledAt: event.target.value }))
                        }
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                        disabled={editSaving}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Reference number
                    </label>
                    <input
                      type="text"
                      value={editForm.referenceNumber}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, referenceNumber: event.target.value }))
                      }
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      disabled={editSaving}
                      placeholder="Optional reference number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    <textarea
                      value={editForm.notes}
                      onChange={(event) =>
                        setEditForm((prev) => ({ ...prev, notes: event.target.value }))
                      }
                      rows={3}
                      className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      placeholder="Optional notes"
                      disabled={editSaving}
                    />
                  </div>
                </>
              )}
              <div className="flex items-center justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  disabled={editSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={editSaving || editLoading}
                >
                  {editSaving ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}

